import { gsap } from "gsap";

/**
 * Handles the accordion horizontal block
 *
 * @author Alexander Uhlemann
 */
export default class AccordionHorizontalHandler {
	/**
	 * Constructor
	 *
	 * Get all dependencies and register the needed event handler
	 *
	 * @author Alexander Uhlemann
	 */
	constructor() {
		/**
		 * This two layouts are used for later purposes, when we have to check which layout is current active on the accordion
		 * Because the style when oppening an item, depends on the layout
		 */
		this.horizontalLayout = "item-horizontal-title";
		this.verticalLayout = "item-vertical-title";

		this.getDependencies();

		if (!this.dependenciesAvailable()) {
			return;
		}

		this.events();
	}

	/**
	 * Retrieves the needed dependencies
	 *
	 * @author Alexander Uhlemann
	 *
	 * @returns {void}
	 */
	getDependencies = () => {
		this.accordion = document.querySelector(".zar-holding-accordion-horizontal");
	};

	/**
	 * Checks if all dependencies exist
	 *
	 * @author Alexander Uhlemann
	 *
	 * @returns {boolean} true if all elements available (not null or undefined), otherwise false
	 */
	dependenciesAvailable = () => {
		if (this.accordion === null || this.accordion === undefined) return false;

		/**
		 * Only if the accordion exists, we are going to check if there are items and a layout
		 * If there are no items or no layout, we are going to return false
		 */
		this.accordionLayout = this.accordion.dataset.layout;
		this.accordionItems = this.accordion.querySelectorAll(".zar-holding-accordion-horizontal__item");
		this.accordionTitleLetters = this.accordion.querySelectorAll(".js__accordion_title_letter");

		if (this.accordionItems === null || this.accordionItems === undefined || this.accordionItems.length <= 0) return false;
		if (this.accordionLayout === null || this.accordionLayout === undefined) return false;
		return true;
	};

	/**
	 * Register needed event handler
	 *
	 * @author Alexander Uhlemann
	 *
	 * @returns {void}
	 */
	events = () => {
		// Listen for clicks on items
		this.accordionItems.forEach((item) => {
			item.addEventListener("click", this.toggleAccordion);
		});

		// Open the first item of the accordion
		document.addEventListener("DOMContentLoaded", this.activateFirstItem);

		// Add media queries to listen for screen size changes
		const mediaQuery = window.matchMedia("(min-width: 1080px)");
		mediaQuery.addEventListener("change", this.handleScreenSizeChange);
	};

	/**
	 * Activates the first item on page load
	 *
	 * This is needed because the first item should be opened by default
	 * We can also use the zero index because we check in dependenciesAvailable() if there is at least one item
	 *
	 * @author Alexander Uhlemann
	 *
	 * @returns {void}
	 */
	activateFirstItem = () => {
		this.currentActiveItem = this.accordionItems[0];
		this.toggleItem(this.currentActiveItem);
	};

	/**
	 * Handles the click event on an accordion item
	 *
	 * @author Alexander Uhlemann
	 *
	 * @param {*} e the event object
	 *
	 * @returns {void}
	 */
	toggleAccordion = (e) => {
		let currentClickedItem = e.currentTarget;

		let queue = [];

		/**
		 * If the current clicked item is the same as the active one, that means
		 * the user has clicked on the opened item, so we are doing nothing
		 */
		if (currentClickedItem === this.currentActiveItem) {
			return;
		}

		/**
		 * If the current clicked item is not the same as the active one
		 */
		if (currentClickedItem !== this.currentActiveItem) {
			/**
			 * This could mean there is currently no active one, so we have to check that
			 * Is the active one is set and not null, we are going to close it
			 */
			if (this.currentActiveItem !== null) {
				this.toggleItem(this.currentActiveItem);
			}

			/**
			 * Now we are going to open the clicked item and set it as the new active one
			 */
			this.toggleItem(currentClickedItem);
			this.currentActiveItem = currentClickedItem;
		}
	};

	/**
	 * Changes the state of an item.
	 * This method then calls either toggleHorizontalLayout or toggleVerticalLayout, depending on the current layout.
	 *
	 * @author Alexander Uhlemann
	 *
	 * @param {*} item
	 *
	 * @returns {void}
	 */
	toggleItem = (item) => {
		if (this.isMobile()) {
			/**
			 * If an item is not active on a mobile device, the mb-8 has to be removed, if the item is active, the mb-8 has to be added
			 * to have a gap between the (title + icon) and the content
			 */
			let header = item.querySelector(".zar-holding-accordion-horizontal__item-header");
			header.classList.toggle("mb-8");

			/**
			 * If the item is active on a mobile device, the chevron shall point upwards, otherwise downwards
			 */
			let chevron = header.querySelector(".zar-holding-accordion-horizontal__item-chevron");
			chevron.classList.toggle("rotate-180");
		}

		if (this.accordionLayout === this.horizontalLayout) {
			this.toggleHorizontalLayout(item);
		}

		if (this.accordionLayout === this.verticalLayout) {
			if (!this.isMobile()) {
				this.toggleVerticalLayout(item);
			}


			/**
			 * In the vertical layout, the accordion can have a title with single letters 
			 * When clicking on an item, the letter of the title should change the color 
			 */
			let itemIndex = item.dataset.itemIndex;
			this.changeTitleLetterColor(itemIndex);
		}

		// Toggles the content hidden status
		let content = item.querySelector(".zar-holding-accordion-horizontal__item-content");
		content.classList.toggle("hidden");

		if (this.accordionLayout === this.verticalLayout) {
			item.classList.toggle("bg-zar-green-light");
		}
	};

	/**
	 * Checks if the current device is a mobile / tablet device ( < 1080px )
	 * It just checks if the screen is smaller then 1080px, if yes, it is a "mobile" device
	 *
	 * @author Alexander Uhlemann
	 *
	 * @returns {boolean} true if the current device is a mobile device, otherwise false
	 */
	isMobile = () => {
		return window.innerWidth < 1080;
	};

	/**
	 * Handles the screen size change. More efficient to use Media Queries instead of the resize event.
	 * If the window starts in desktop size and then shrinks to mobile size, the mb-8 is missing.
	 * Because on mobile each item does not have mb-8 by default, only if the item is opened.
	 *
	 * @author Alexander Uhlemann
	 *
	 * @returns {void}
	 */
	handleScreenSizeChange = () => {
		if (this.currentActiveItem !== null) {
			let header = this.currentActiveItem.querySelector(".zar-holding-accordion-horizontal__item-header");

			if (this.isMobile()) {
				header.classList.add("mb-8");

				// If it was desktop before, the icon of the active element should be not visible, here we make it visible again
				let itemIcon = header.querySelector(".zar-holding-accordion-horizontal__item-icon");
				itemIcon.classList.remove("hidden");
			} else {
				header.classList.remove("mb-8");
			}

			if (this.accordionLayout === this.verticalLayout) {
				this.toggleVerticalLayout(this.currentActiveItem);
			}

			if (this.accordionLayout === this.horizontalLayout) {
				this.toggleHorizontalLayout(this.currentActiveItem);
			}
		}
	};

	/**
	 * This method gets called when the screen size changes, the first item gets activated or the user clicks an item.
	 * For example the screen size changes from desktop to mobile, then we have to hide the itom-icon-activate and item-title-activate element
	 * and show the item-icon and item-title element, which are placed above the content.
	 * It is exactly like the toggleHorizontalLayout Method, just the design changes differently. But the logic is the same.
	 * Here is also toggle used, so everything vice versa.
	 *
	 * @param {*} item
	 */
	toggleVerticalLayout = (item) => {

		item.classList.toggle("grid");

		let header = item.querySelector(".zar-holding-accordion-horizontal__item-header");
		header.classList.toggle("hidden");

		let itemTitleActive = item.querySelector(".zar-holding-accordion-horizontal__item-title-active");
		itemTitleActive.classList.toggle("hidden");

		let itemIconActive = item.querySelector(".zar-holding-accordion-horizontal__item-icon-active");
		itemIconActive.classList.toggle("hidden");

	};

	/**
	 * This method gets called when the screen size changes, the first item gets activated or the user clicks an item.
	 * It either activates an item or it deactivates an item. Because we are using toggle here.
	 * For example the screen size changes from desktop to mobile, then we have to hide the arrowWrapper and show the icon.
	 * The same for the case, the user clicks an item manually, we have to hide from the last selected item the arrowWrapper and show the icon.
	 * And because of toggle, everything vice versa. The user opens an item, the icon gets hidden and the arrowWrapper gets shown.
	 *
	 * @param {*} item
	 */
	toggleHorizontalLayout = (item) => {
		let header = item.querySelector(".zar-holding-accordion-horizontal__item-header");

		if (!this.isMobile()) {
			/**
			 * If the item is inactive, the icon is above the title both the icon and title are wrapped in a container.
			 * If the item is active, we are going to hide the icon but the title will remain visible.
			 * We are making the item-arrow-wrapper visible, that includes an arrow and the icon (active) again.
			 */
			let itemIcon = header.querySelector(".zar-holding-accordion-horizontal__item-icon");
			itemIcon.classList.toggle("hidden");
		}

		/**
		 * The itemTitle is in inactive state vertical, but when transition to the active state,
		 * the itemTitle has to be horizontal. And vice versa when transition from active to inactive
		 */
		let itemTitle = header.querySelector(".zar-holding-accordion-horizontal__item-title");
		itemTitle.classList.toggle("lg:writing-mode-horizontal-tb");
		itemTitle.classList.toggle("lg:-rotate-180");

		/**
		 * The Arrow Wrapper wraps the arrow and the icon active
		 */
		let arrowWrapper = item.querySelector(".zar-holding-accordion-horizontal__item-arrow-wrapper");
		arrowWrapper.classList.toggle("lg:flex");
		arrowWrapper.classList.toggle("lg:hidden");

		/**
		 * The itemHeaderContentWrapper wraps the title and the content.
		 * This allows us to setup a padding-left with the same width as the icon + the gap-x between the icon and the arrow.
		 * This results in that the content of the item has the same x start then the arrow above and the icon is left of the content.
		 */
		let itemHeaderContentWrapper = item.querySelector(".zar-holding-accordion-horizontal__item-header-content-wrapper");
		itemHeaderContentWrapper.classList.toggle("lg:pl-14");
	};

	/**
	 * Changes the color of the letter of the title, depending on the index
	 * 
	 * @param {*} index
	 * 
	 * @returns {void}
	 * 
	 * @since 1.0.0
	 * @author Christian Werner 
	 */
	changeTitleLetterColor = (index) => {
		if (this.accordionTitleLetters === null || this.accordionTitleLetters === undefined) return;

		// reset all letters to text-white 
		this.accordionTitleLetters.forEach((letter) => {
			letter.classList.remove("!text-zar-green-light");

			if (letter.dataset.letterIndex == index) {
				letter.classList.add("!text-zar-green-light");
			}

		});

		// let letterElement = this.accordionTitleLetters[index];
		// letterElement.classList.add("!text-zar-green-light");
	}
}
